import { queryClient } from "@istari-frontend/api-client";
import { useMutation } from "@tanstack/react-query";

import { systemsQueryKeys } from "~/api/useSystems";
import { commitAtCurrentHead } from "~/branching/feature/commit-at-current-head";
import { useInvalidateSystemBranching } from "~/branching/feature/use-invalidate-system-branching";
import type { BranchId } from "~/branching/model/branch";
import type { CommitId } from "~/branching/model/commit";
import { isProtectedBranchProblem } from "~/libraries/model/publishing-error";

type CommitFileToBranchParams = {
  branchId: BranchId;
  expectedHeadSnapshotId: CommitId;
  fileRevisionId: string;
  commitMessage: string;
};

/** Pin one file revision into a branch; the server keeps everything else at the head. */
export function useCommitFileToBranch(systemId: string | undefined) {
  const invalidateSystemBranching = useInvalidateSystemBranching();
  return useMutation({
    mutationFn: (params: CommitFileToBranchParams) => {
      if (!systemId) throw new Error("systemId is required to commit a file to a branch");
      return commitAtCurrentHead(systemId, params.branchId, params.expectedHeadSnapshotId, () => ({
        kind: "put",
        message: params.commitMessage,
        fileRevisionIds: [params.fileRevisionId],
      }));
    },
    onSuccess: () => {
      if (!systemId) return;
      void invalidateSystemBranching(systemId);
      void queryClient.invalidateQueries({ queryKey: systemsQueryKeys.item(systemId) });
    },
    onError: (error) => {
      if (systemId && isProtectedBranchProblem(error)) void invalidateSystemBranching(systemId);
    },
  });
}
